import { ChartType } from 'ng-apexcharts';
import {
  ChartDataSource,
  ChartVisualizationConfig,
  ChartVisualizationConfigSeries,
  ChartVisualizationTypes,
} from '../models';
import { ReportPeriodUtils } from './report-period.utils';
import { CategoryBalance } from '../models/category-balance';
import { TimeSeriesChartDataResponse } from '../../properties/models/time-series-chart';

export namespace ChartRendererUtils {
  export const visualizationOptions = [
    { label: 'Bar', value: 'bar' },
    { label: 'Mixed', value: 'mixed' },
  ];

  const dataLabelsPrefixesAndSuffixes = {
    money: ['$', ''],
    percent: ['', '%'],
    number: ['', ''],
  };

  export const generateRandomColor = () => {
    const randomColor = Math.floor(Math.random() * 16777215).toString(16);
    const randomColorAppend = randomColor.length < 6 ? '0'.repeat(6 - randomColor.length) : '';

    return `#${randomColor + randomColorAppend}`;
  };

  const chartMap: { [type in ChartVisualizationTypes]: ChartType } = {
    bar: 'bar',
    funnel: 'bar',
    pie: 'pie',
    mixed: 'line',
  };

  export const seriesFactory = (visualizationConfig: ChartVisualizationConfig, dataSource: ChartDataSource) => {
    return visualizationConfig.series.reduce((acc, { name, color, fieldId, type, format }) => {
      let data = dataSource.rows[fieldId]?.data;

      if (!data) {
        data = Array.from({ length: dataSource.categories.length }, () => 0);
      }

      if (visualizationConfig.meta.reversed) {
        data = data.slice(0).reverse();
      }

      return [
        ...acc,
        {
          name,
          color,
          type,
          data,
        },
      ];
    }, []);
  };

  const chartFactory = (type: ChartVisualizationTypes, stacked = false) => {
    const chartType = chartMap[type];

    return {
      height: '100%',
      type: chartType,
      stacked,
      toolbar: {
        show: true,
      },
      zoom: {
        enabled: true,
        type: <any>'x',
        autoScaleYaxis: true,
        allowMouseWheelZoom: true,
      },
    };
  };

  const xaxisFactory = (categories: (string | number)[]) => {
    return {
      categories,
    };
  };

  const getAbbreviatedNumbers = (val, prefix = '', suffix = '') => {
    const num = Number(val);
    if (isNaN(num)) return val;
    const absNum = Math.abs(num);

    if (absNum >= 1_000_000) {
      const formatted = (absNum / 1_000_000).toFixed(1) + 'M';
      return `${num < 0 ? '-' : ''}${prefix}${formatted}${suffix}`;
    }

    if (absNum >= 1000) {
      const formatted = (absNum / 1000).toFixed(1) + 'K';
      return `${num < 0 ? '-' : ''}${prefix}${formatted}${suffix}`;
    }

    return `${prefix}${num.toFixed(2)}${suffix}`;
  };

  const dataLabelsFactory = (visualizationSeries: ChartVisualizationConfig['series']) => {
    const indexesOfSeriesWithEnabledLabels = visualizationSeries
      .map((series, index) => (series.displayLabels ? index : -1))
      .filter((index) => index !== -1);
    const background = {
      enabled: true,
      foreColor: '#fff',
      padding: 4,
      borderRadius: 2,
      borderWidth: 1,
      borderColor: 'inherit',
      opacity: 0.9,
      dropShadow: {
        enabled: false,
        top: 1,
        left: 1,
        blur: 1,
        color: '#000',
        opacity: 0.45,
      },
    };
    const colors = visualizationSeries.map((series) => series?.color);

    return {
      enabled: true,
      enabledOnSeries: indexesOfSeriesWithEnabledLabels,
      style: {
        fontSize: '12px',
        fontFamily: 'Inter, Arial, sans-serif',
        colors,
      },
      formatter: (val, { seriesIndex }) => {
        const seriesConfiguration: ChartVisualizationConfigSeries = visualizationSeries[seriesIndex];
        const format = seriesConfiguration?.format || 'number';

        return getAbbreviatedNumbers(val, ...dataLabelsPrefixesAndSuffixes[format]);
      },
      background,
    };
  };

  const plotOptionsFactory = ({ direction, type }: ChartVisualizationConfig['meta']) => {
    switch (type) {
      case 'mixed':
        return null;
      default:
        return {
          bar: {
            horizontal: direction === 'horizontal',
            dataLabels: {
              total: {
                enabled: true,
                offsetY: -8,
                style: {
                  fontSize: '14px',
                  fontFamily: undefined,
                  fontWeight: 600,
                },
              },
            },
          },
        };
    }
  };

  const yaxisFactory = (visualizationConfigSeries: ChartVisualizationConfig['series']) => {
    return {
      labels: {
        formatter: (val) => {
          // the Y-axis gets the format from the first series, even though there can be multiple series
          // using different formats
          const format = visualizationConfigSeries[0]?.format || 'number';

          return getAbbreviatedNumbers(val, ...dataLabelsPrefixesAndSuffixes[format]);
        },
      },
    };
  };

  const tooltipFactory = (visualizationConfigSeries: ChartVisualizationConfig['series']) => {
    return {
      enabled: true,
      y: {
        formatter: (val, { seriesIndex }) => {
          const numberVal = Number(val);

          if (isNaN(numberVal)) return val;

          const seriesConfiguration: ChartVisualizationConfigSeries = visualizationConfigSeries[seriesIndex];
          const format = seriesConfiguration?.format || 'number';
          const [prefix, suffix] = dataLabelsPrefixesAndSuffixes[format];

          return numberVal < 0
            ? `-${prefix}${Math.abs(numberVal).toFixed(2)}${suffix}`
            : `${prefix}${numberVal.toFixed(2)}${suffix}`;
        },
      },
    };
  };

  export const buildChart = (visualizationConfig: ChartVisualizationConfig, dataSource: ChartDataSource) => {
    const series = seriesFactory(visualizationConfig, dataSource);
    const chart = chartFactory(visualizationConfig.meta.type, visualizationConfig.meta.stacked);
    const plotOptions = plotOptionsFactory(visualizationConfig.meta);

    const categories = visualizationConfig.meta.reversed
      ? dataSource.categories.slice(0).reverse()
      : dataSource.categories;
    const xaxis = xaxisFactory(categories);
    const yaxis = yaxisFactory(visualizationConfig.series);

    const dataLabels = dataLabelsFactory(visualizationConfig.series);
    const tooltip = tooltipFactory(visualizationConfig.series);

    return {
      series,
      chart,
      plotOptions,
      dataLabels,
      xaxis,
      yaxis,
      tooltip,
    };
  };

  export const getDummyChartDataSource = (chartVisualizationConfig: ChartVisualizationConfig): ChartDataSource => {
    const categories = ReportPeriodUtils.buildCategoriesFromReportPeriod(
      ReportPeriodUtils.buildReportPeriod({ key: chartVisualizationConfig.reportPeriod.key }),
    );
    const rows = chartVisualizationConfig.series.reduce((acc, { fieldId, name }) => {
      const data = Array.from({ length: categories.length }, () => Math.floor(Math.random() * 100));

      acc[fieldId] = { name, data };

      return acc;
    }, {});

    return {
      rows,
      categories,
    };
  };

  export const convertCategoryBalanceToChartData = (
    reportPanelData: CategoryBalance[],
    reportPeriodRange: CategoryBalance['accounting_period'][],
  ): ChartDataSource => {
    const categories = reportPeriodRange;
    const rows = reportPanelData.reduce(
      (acc, reportData) => {
        const amountAsNumber = Number(reportData.amount);
        const indexOfCategory = categories.indexOf(reportData.accounting_period);

        if (isNaN(amountAsNumber)) {
          console.error(`Amount is not a number: ${JSON.stringify(reportData, null, 2)}`);
          return acc;
        }

        if (acc[reportData.category_id]) {
          acc[reportData.category_id].data[indexOfCategory] = amountAsNumber;
          return { ...acc };
        }

        const newSeriesData = new Array(categories.length).fill(0);

        newSeriesData[indexOfCategory] = amountAsNumber;

        return {
          ...acc,
          [reportData.category_id]: {
            name: reportData.category_name,
            data: newSeriesData,
          },
        };
      },
      {} as {
        [fieldId: number]: {
          name: string;
          data: number[];
        };
      },
    );

    return {
      rows,
      categories,
    };
  };

  export const convertPropertyFieldsToChartData = (
    reportPanelData: TimeSeriesChartDataResponse['results'],
    reportPeriodRange: TimeSeriesChartDataResponse['period_range'],
  ): ChartDataSource => {
    const categories = reportPeriodRange;
    const rows = reportPanelData.reduce(
      (acc, reportData) => {
        const amountAsNumber = Number(reportData.field_value);
        const indexOfCategory = categories.indexOf(reportData.period);

        if (isNaN(amountAsNumber)) {
          console.error(`Amount is not a number: ${JSON.stringify(reportData, null, 2)}`);
          return acc;
        }

        if (acc[reportData.field_id]) {
          acc[reportData.field_id].data[indexOfCategory] = amountAsNumber;
          return { ...acc };
        }

        const newSeriesData = new Array(categories.length).fill(0);

        newSeriesData[indexOfCategory] = amountAsNumber;

        return {
          ...acc,
          [reportData.field_id]: {
            name: reportData.field_label,
            data: newSeriesData,
          },
        };
      },
      {} as {
        [fieldId: number]: {
          name: string;
          data: number[];
        };
      },
    );

    return {
      rows,
      categories,
    };
  };
}
